import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Camera, ScanLine, ShieldAlert, SlidersHorizontal, Lightbulb, ChevronRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
interface Tip {
  ingredient: string;
  why: string;
  found: string;
}
const tips: Tip[] = [{
  ingredient: 'Red 40 & Yellow 5/6',
  why: 'Artificial food dyes linked to hyperactivity in children and possible carcinogenic effects. Banned or restricted in several countries.',
  found: 'Candy, soft drinks, cereals, snack chips'
}, {
  ingredient: 'High-Fructose Corn Syrup',
  why: 'A highly processed sweetener tied to obesity, insulin resistance, and fatty liver disease when consumed in excess.',
  found: 'Sodas, sauces, baked goods, flavored yogurt'
}, {
  ingredient: 'Monosodium Glutamate (MSG)',
  why: 'A flavor enhancer that some people are sensitive to, potentially causing headaches and flushing. Best consumed in moderation.',
  found: 'Instant noodles, chips, frozen meals, soups'
}, {
  ingredient: 'Sodium Benzoate',
  why: 'A preservative that can form benzene (a known carcinogen) when combined with vitamin C in acidic drinks.',
  found: 'Fruit juices, soft drinks, salad dressings'
}, {
  ingredient: 'Partially Hydrogenated Oils (Trans Fats)',
  why: 'Raise "bad" LDL cholesterol while lowering "good" HDL cholesterol, significantly increasing heart disease risk.',
  found: 'Margarine, fried foods, packaged pastries'
}, {
  ingredient: 'Mono & Diglycerides',
  why: 'Emulsifiers that can hide trans fats and may disrupt gut bacteria with frequent consumption.',
  found: 'Bread, ice cream, peanut butter, coffee creamer'
}];
const steps = [{
  icon: Camera,
  title: 'Step 1 — Open the Scanner',
  image: 'https://cdn.magicpatterns.com/uploads/1W1J6e82j7au2pvoFPkTsS/IMG_4140_2.png',
  alt: 'IntelliEats home screen with Camera, Gallery, and Barcode scan options',
  body: 'From the home screen, choose how you want to scan. Tap Camera to photograph the ingredient label directly, Gallery to upload an existing photo, or Barcode to look up a product. Point your camera at the ingredient list — not the front of the package — and make sure the text is well-lit and in focus. Remember: the accuracy of detection depends on the quality of the image.'
}, {
  icon: ScanLine,
  title: 'Step 2 — Let IntelliEats Analyze',
  image: 'https://cdn.magicpatterns.com/uploads/os2N2XDcSvLjNDhdQRq49C/IMG_4142.png',
  alt: 'Scan Complete modal showing a quick summary of ingredients to avoid',
  body: 'Once you capture the label, IntelliEats reads the text and instantly analyzes every ingredient. You will see a Scan Complete summary that tells you at a glance whether any Ingredients to Avoid or Limited Intake ingredients were found. Tap View Results to dig into the full breakdown.'
}, {
  icon: ShieldAlert,
  title: 'Step 3 — Review Your Results',
  image: 'https://cdn.magicpatterns.com/uploads/6uZ6QUuqTgkVvEx3WusCKk/IMG_4145.png',
  alt: 'Results screen listing ingredients to avoid like Red 40 and Yellow 6',
  body: 'The results screen clearly separates Ingredients to Avoid (shown in red, such as Red 40, Yellow 6, Yellow 5, and Dextrin) from Limited Intake Ingredients (such as Monosodium glutamate). Tap any highlighted ingredient to learn why it was flagged and how it may affect your health. These two categories are always free.'
}, {
  icon: SlidersHorizontal,
  title: 'Step 4 — Personalize Detection Settings',
  image: 'https://cdn.magicpatterns.com/uploads/two.png',
  alt: 'Detection settings screen with dietary restriction toggles',
  body: 'Head to Settings to tailor the scanner to your own diet. Enable detection for dietary restrictions like Vegetarian, Vegan, Nuts, Gluten, Lactose, and Keto so IntelliEats flags ingredients that conflict with how you eat. Now every scan is personalized to you.'
}];
export function HowToGuide() {
  const [tipIndex, setTipIndex] = useState(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'How to Use IntelliEats to Scan Your Pantry | IntelliEats';
  }, []);
  const tip = tips[tipIndex];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-2281" data-mp="2egu5ro1radsm">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2282" data-mp="1lrwsy0gbd979">
        {/* Header */}
        <header className="mb-12" data-id="element-2283" data-mp="1lpkehx9rlgyy">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-2284" data-mp="1qqq3pl47um3q">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2285" data-mp="y2lx8xdtm4zz" />
            Back to Home
          </Link>
          <div className="inline-block px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold tracking-wider uppercase mb-6" data-id="element-2286" data-mp="131fbis0xm017">
            How-To Guide
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-2287" data-mp="vftm4r68a11q">
            How to Use IntelliEats to Scan Your Pantry
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2288" data-mp="li5hn9itlf6d">
            Scan a product label and instantly detect unhealthy ingredients and
            dietary-restriction conflicts in everything sitting in your pantry.
            This step-by-step guide walks you through the entire process.
          </p>
        </header>

        {/* Intro */}
        <div className="prose prose-lg prose-slate max-w-none mb-12" data-id="element-2289" data-mp="2sz400wvvdrf">
          <p className="text-slate-700 leading-relaxed" data-id="element-2290" data-mp="2r4fydqbf5j4">
            Your pantry is full of packaged foods, and every one of them carries
            an ingredient label that can be hard to decode. Long chemical names,
            hidden sugars, and artificial additives make it nearly impossible to
            tell at a glance whether something is good for you. IntelliEats was
            built to solve exactly this problem. Instead of squinting at tiny
            print and searching the internet for unfamiliar ingredient names,
            you simply point your phone at the label and let the app do the
            heavy lifting. In just a few seconds you will know which ingredients
            are safe, which ones you should limit, and which ones to avoid
            entirely.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-2291" data-mp="3u43gheln95ov">
            The process is designed to be effortless. Whether you are doing a
            full pantry clean-out, comparing two products at the grocery store,
            or just curious about that snack in your bag, the workflow is the
            same: scan, analyze, and decide. Below, we break down each step so
            you can get the most out of every scan.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-12 mb-16" data-id="element-2292" data-mp="lnh2kpe8uh2c">
          {steps.map((step, index) => <motion.div key={step.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.05
        }} className="grid sm:grid-cols-2 gap-8 items-center" data-id="element-2293" data-mp="uzeoqlywm6fs" data-mpi={index}>
              <div className={index % 2 === 1 ? 'sm:order-2' : ''} data-id="element-2294" data-mp="81ur54vmuw8l">
                <div className="flex items-center gap-3 mb-3" data-id="element-2295" data-mp="m0b61j3nft1t">
                  <span className="bg-brand-100 text-brand-600 p-2 rounded-xl" data-id="element-2296" data-mp="h0y925j2d5li">
                    <step.icon className="w-5 h-5" data-id="element-2297" data-mp="h318fg63wb2w" />
                  </span>
                  <h2 className="text-xl font-bold text-slate-900 m-0" data-id="element-2298" data-mp="1xdn2va0zrbeh">
                    {step.title}
                  </h2>
                </div>
                <p className="text-slate-700 leading-relaxed" data-id="element-2299" data-mp="3mwlmsg0iownl">{step.body}</p>
              </div>
              <div className={index % 2 === 1 ? 'sm:order-1' : ''} data-id="element-2300" data-mp="3qxx5e1kz4hma">
                <img src={step.image} alt={step.alt} className="w-full max-w-[260px] mx-auto rounded-3xl shadow-xl border border-slate-100" data-id="element-2301" data-mp="3qy6ulmy72kqx" />
              </div>
            </motion.div>)}
        </div>

        {/* Why It Matters */}
        <div className="prose prose-lg prose-slate max-w-none mb-16" data-id="element-2302" data-mp="9jrzfo1iieed">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2303" data-mp="eeywid0a0kub">
            Detecting Unhealthy & Dietary-Restriction Ingredients
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2304" data-mp="ngyfhauupyzw">
            IntelliEats does more than spot generally "unhealthy" additives. It
            also understands your personal dietary needs. If you are vegetarian,
            vegan, keto, or managing allergies to nuts, gluten, or lactose, the
            app will flag any ingredient that conflicts with your chosen
            restrictions. This means a single scan protects you from both
            long-term health risks — like artificial dyes and trans fats — and
            immediate concerns like an allergen hiding under an unfamiliar name.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-2305" data-mp="230xo0qi89aib">
            By scanning everything in your pantry once, you build a clear
            picture of what you and your family are really eating. Over time,
            you will start to recognize the worst offenders on your own, shop
            smarter, and replace problematic products with cleaner alternatives.
            Healthier choices start with understanding what is really inside the
            package — and IntelliEats makes that understanding instant.
          </p>
        </div>

        {/* Tip of the Day */}
        <div className="bg-gradient-to-br from-accent-50 to-brand-50 rounded-3xl p-8 sm:p-10 border border-accent-100" data-id="element-2306" data-mp="1b98c7b3g9kng">
          <div className="flex items-center gap-3 mb-6" data-id="element-2307" data-mp="3k0cb5aaabntd">
            <span className="bg-accent-400 text-white p-2 rounded-xl" data-id="element-2308" data-mp="3jyre9e0hzssc">
              <Lightbulb className="w-6 h-6" data-id="element-2309" data-mp="3g13iy9uw7xzx" />
            </span>
            <h2 className="text-2xl font-bold text-slate-900 m-0" data-id="element-2310" data-mp="3tr6xeenj6net">
              Tip of the Day
            </h2>
          </div>

          <AnimatePresence mode="wait" data-id="element-2311" data-mp="3oynk7isr9uaj">
            <motion.div key={tipIndex} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-id="element-2312" data-mp="7jp5jyqryrhu">
              <div className="flex items-center gap-2 mb-3" data-id="element-2313" data-mp="2dcoy3y6q83ff">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full" data-id="element-2314" data-mp="2dei3zb9imjmd">
                  <AlertTriangle className="w-3 h-3" data-id="element-2315" data-mp="xtpu5a5y9at7" />
                  AVOID
                </span>
                <h3 className="text-lg font-bold text-slate-900 m-0" data-id="element-2316" data-mp="k2b3qi9pq7c4">
                  {tip.ingredient}
                </h3>
              </div>
              <p className="text-slate-700 mb-3" data-id="element-2317" data-mp="o2lgcds0fvzy">{tip.why}</p>
              <p className="text-sm text-slate-500 m-0" data-id="element-2318" data-mp="a4eq9jtaz0au">
                <span className="font-semibold text-slate-700" data-id="element-2319" data-mp="a2tx9ymmzpf3">
                  Commonly found in:
                </span>{' '}
                {tip.found}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-6" data-id="element-2320" data-mp="552656ttg7by">
            <span className="text-sm text-slate-500" data-id="element-2321" data-mp="55by2zyf8oka">
              Tip {tipIndex + 1} of {tips.length}
            </span>
            <button onClick={() => setTipIndex(prev => (prev + 1) % tips.length)} className="inline-flex items-center px-4 py-2 bg-white border border-brand-200 text-brand-700 text-sm font-semibold rounded-full hover:bg-brand-50 transition-colors" data-id="element-2322" data-mp="1vfnb334ol831">
              Next Tip
              <ChevronRight className="w-4 h-4 ml-1" data-id="element-2323" data-mp="2zf20kh3nxwhr" />
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12" data-id="element-2324" data-mp="2uh0fjqmofn3n">
          <Link to="/#download" className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-full hover:bg-brand-700 transition-colors shadow-lg shadow-brand-200/50" data-id="element-2325" data-mp="2uibnm2n5nh0k">
            Download IntelliEats & Scan Your Pantry
          </Link>
        </div>
      </article>
    </motion.div>;
}